import React, { FC } from "react";
import { TourItinerary } from "@graphql/types";
import { Trans } from "@helpers/translate";
import { convertTimeTo12HourFormat } from "@helpers/getTimeFormat";
import { Paragraph } from "@components/ui/Paragraph";
import { Title } from "@components/ui/Title";
import { ShowMore } from "@components/ui/ShowMore";
import { twMerge } from "tailwind-merge";

interface ItineraryItemProps extends TourItinerary {
  displayLine?: boolean;
}

const ItineraryItem: FC<ItineraryItemProps> = ({
  title,
  time,
  type,
  description,
  displayLine = true,
}) => {
  // For meeting point slightly different ui is shown
  if (type === "meeting_point") {
    return (
      <div className="mt-2 flex justify-between overflow-hidden">
        <div className="flex justify-center flex-wrap w-6">
          <div className="w-full text-center">
            <div className="w-3 h-3 bg-primary rounded-full inline-block" />
          </div>
          {displayLine && <div className="w-0.5 bg-primary h-full mt-1" />}
        </div>
        <div className="pb-6 ml-2 w-full">
          {time && (
            <div className="bg-primary text-white rounded-full inline-block px-3 text-sm mb-2">
              {convertTimeTo12HourFormat(time)}
            </div>
          )}

          {title && (
            <p className="text-lg font-medium leading-normal mb-2 -mt-0.5 text-black">
              {"Meeting Point"}
            </p>
          )}

          <Paragraph size="md" className="mb-0">
            {title}
          </Paragraph>

          {description && (
            <ShowMore className="mb-0 ml-2">
              <Paragraph size="md" className="pr-2">
                - {description}
              </Paragraph>
            </ShowMore>
          )}
        </div>
      </div>
    );
  }

  // For other itinerary items
  return (
    <div className="mt-2 flex justify-between overflow-hidden">
      <div className="flex justify-center flex-wrap w-6">
        <div className="w-full text-center">
          <div className="w-3 h-3 bg-primary rounded-full inline-block" />
        </div>
        {displayLine && <div className="w-0.5 bg-primary h-full mt-1" />}
      </div>
      <div className="pb-6 ml-2 w-full">
        {type != "ending_point" && time && (
          <div className="bg-primary text-white rounded-full inline-block px-3 text-sm mb-2">
            {convertTimeTo12HourFormat(time)}
          </div>
        )}

        {title && (
          <p className="text-lg font-medium leading-normal mb-2 -mt-0.5 text-black">
            {title}
          </p>
        )}

        <Paragraph size="md" className="mb-0 pr-2">
          {description}
        </Paragraph>
      </div>
    </div>
  );
};

interface Props {
  itinerary: TourItinerary[];
  showTitle?: boolean;
  showSubTitle?: boolean;
  className?: string;
}

export const Itinerary: FC<Props> = ({
  itinerary,
  showTitle = false,
  showSubTitle = false,
  className = "",
}) => {
  return (
    <div className={twMerge("mb-20", className)}>
      {showTitle && (
        <Title as="h2" size="md" divider className="mb-6">
          <Trans id="tour.block.itinerary.title">Itinerary</Trans>
        </Title>
      )}

      {showSubTitle && (
        <Paragraph
          size="md"
          className="m-0 mb-10 font-normal leading-normal max-w-screen-lg text-gray-700 text-base md:text-lg"
        >
          <Trans id="tour.block.itinerary.description">
            This itinerary can be customized to meet your needs and preferences.
            Send me a message with your requests!
          </Trans>
        </Paragraph>
      )}

      {itinerary.map((item, index) => (
        <ItineraryItem
          key={index}
          {...item}
          displayLine={index + 1 !== itinerary.length}
        />
      ))}
    </div>
  );
};
